import React, { useEffect, useMemo, useState } from "react";
import Header from "../component/Header";
import Footer from "../component/Footer";
import { API_BASE_URL } from "../utils/app_url";
import { NotificationManager } from "react-notifications";
import { useLanguage } from "../context/LanguageContext";

const DEFAULT_FORM = {
    company_name: "",
    name: "",
    email: "",
    phone: "",
    message: "",
};

const ContactUs = () => {
    const { langJson } = useLanguage();
    const [form, setForm] = useState(DEFAULT_FORM);
    const [saving, setSaving] = useState(false);
    const [allDetails, setAllDetails] = useState([]);

    const whatsappText = useMemo(() => {
        const lines = [
            `Company: ${form.company_name || "-"}`,
            `Name: ${form.name || "-"}`,
            `Email: ${form.email || "-"}`,
            `Phone: ${form.phone || "-"}`,
            "Message:",
            form.message || "-",
        ];
        return lines.join("\n");
    }, [form]);

    const whatsappConfigValue = useMemo(() => {
        const whatsappValue = allDetails?.find(item => item.name === "whatsapp")?.value;
        if (whatsappValue) {
            return whatsappValue;
        }
        return allDetails?.find(item => item.name === "twitter")?.value || "";
    }, [allDetails]);

    const whatsappUrl = useMemo(() => {
        const value = (whatsappConfigValue || "").trim();
        if (!value) return "";
        if (/^https?:\/\//i.test(value)) return value;
        if (/^\d{6,}$/i.test(value)) return `https://wa.me/${value}`;
        if (/^[A-Za-z0-9]{10,}$/i.test(value)) return `https://api.whatsapp.com/message/${value}`;
        return "";
    }, [whatsappConfigValue]);

    const fetchConfigDetails = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}frontend/configs`, {
                method: "GET",
            });
            const responseData = await response.json();
            if (responseData?.res) {
                setAllDetails(responseData.data || []);
            }
        } catch (error) { }
    };

    useEffect(() => {
        fetchConfigDetails();
    }, []);

    const onFieldChange = event => {
        const { name, value } = event.target;

        setForm(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const validateForm = () => {
        if (!form.company_name.trim()) {
            NotificationManager.warning(langJson?.companyisrequired || "Company is required");
            return false;
        }
        if (!form.name.trim()) {
            NotificationManager.warning(langJson?.fullnameisrequired || "Name is required");
            return false;
        }
        if (!form.email.trim()) {
            NotificationManager.warning(langJson?.emailisrequired || "Email is required");
            return false;
        }
        if (!isValidEmail(form.email.trim())) {
            NotificationManager.warning(langJson?.emailisnotvalid || "Email is not valid");
            return false;
        }
        if (!form.phone.trim()) {
            NotificationManager.warning(langJson?.phoneisrequired || "Phone is required");
            return false;
        }
        if (!form.message.trim()) {
            NotificationManager.warning("Message is required");
            return false;
        }
        if (form.message.trim().length < 10) {
            NotificationManager.warning("Message must be at least 10 characters");
            return false;
        }
        return true;
    };

    const onSubmit = async event => {
        event.preventDefault();
        if (!validateForm() || saving) {
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}frontend/contact-us`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    company_name: form.company_name.trim(),
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    message: form.message.trim(),
                }),
            });

            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                NotificationManager.success(responseData?.msg || "Message sent successfully");
                setForm(DEFAULT_FORM);
            } else {
                NotificationManager.warning(responseData?.msg || "Failed to send message");
            }
        } catch (error) {
            NotificationManager.error("Something went wrong");
        } finally {
            setSaving(false);
        }
    };

    const onWhatsappClick = () => {
        if (!whatsappUrl) {
            return;
        }
        const separator = whatsappUrl.includes("?") ? "&" : "?";
        const url = `${whatsappUrl}${separator}text=${encodeURIComponent(whatsappText)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div>
            <Header />
            <div className="registerMainDiv">
                <div className="registerDiv">
                    <h2>{langJson?.contactus || "Contact Us"}</h2>
                    <p>{langJson?.getintouchwithus || "Get in touch with us"}</p>

                    <form onSubmit={onSubmit}>
                        <ul>
                            <li>
                                <input type="text" name="company_name" placeholder={langJson?.company || "Company name"} value={form.company_name} onChange={onFieldChange} />
                            </li>
                            <li>
                                <input type="text" name="name" placeholder={langJson?.fullname || "Name"} value={form.name} onChange={onFieldChange} />
                            </li>
                            <li>
                                <input type="text" name="email" placeholder={langJson?.emailaddress || "Email address"} value={form.email} onChange={onFieldChange} />
                            </li>
                            <li>
                                <input type="text" name="phone" placeholder={langJson?.phonefaxmobilenumber || "Phone/Fax/Mobile number"} value={form.phone} onChange={onFieldChange} />
                            </li>
                            <li className="fullWidth">
                                <textarea name="message" placeholder={langJson?.message || "Message"} value={form.message} onChange={onFieldChange} rows="5" />
                            </li>
                            <li>
                                <button type="submit" disabled={saving}>{saving ? "Sending..." : (langJson?.send || "Send")}</button>
                                {whatsappUrl ? <button className="whatsAppBtn" type="button" onClick={onWhatsappClick}>Whatsapp</button> : null}
                            </li>
                        </ul>
                    </form>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ContactUs;